import { useEffect, useRef, useState } from "react";
import { hasAnalyticsConsent, setAnalyticsConsent } from "@/lib/seo";

// GDPR-style cookie-consent UI. Until the visitor makes an EXPLICIT choice, no Google tag is loaded
// at all (see AnalyticsLoader). Accept -> lazy-load GA (analytics only, no ads); Decline -> nothing
// loads. The choice persists in localStorage and is always revocable via the persistent
// "Analytics settings" button, which re-opens the banner so the visitor can change their mind.
export function ConsentBanner() {
  const [consent, setConsent] = useState(undefined); // undefined = loading, null = undecided
  const [reopen, setReopen] = useState(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    setConsent(hasAnalyticsConsent());
  }, []);

  const showBanner = consent === null || reopen;

  // Reserve normal-flow space equal to the fixed banner's height so it never covers the footer
  // or floating controls when the visitor scrolls to the bottom. Cleared once a choice is made.
  useEffect(() => {
    if (!showBanner) { document.body.style.paddingBottom = ""; return undefined; }
    const el = bannerRef.current;
    if (!el) return undefined;
    const apply = () => { document.body.style.paddingBottom = `${el.offsetHeight + 16}px`; };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => { ro.disconnect(); document.body.style.paddingBottom = ""; };
  }, [showBanner]);

  if (consent === undefined) return null; // avoid hydration/prerender flash before we know the choice

  const choose = (granted) => {
    setAnalyticsConsent(granted);
    setConsent(granted ? "granted" : "denied");
    setReopen(false);
  };

  if (!showBanner) {
    // Persistent, unobtrusive control so the decision can always be reopened/revoked.
    return (
      <button
        type="button"
        data-testid="analytics-settings-button"
        onClick={() => setReopen(true)}
        className="fixed bottom-3 left-3 z-50 rounded-full border border-black/10 bg-white/90 px-3 py-1.5 text-xs font-semibold text-foreground/70 shadow-md backdrop-blur transition-colors hover:bg-white hover:text-foreground"
      >
        Analytics settings
      </button>
    );
  }

  return (
    <div
      ref={bannerRef}
      data-testid="consent-banner"
      className="fixed inset-x-2 bottom-2 z-[60] mx-auto flex max-w-md items-center gap-3 rounded-xl border border-black/10 bg-white/95 p-3 shadow-[0_8px_30px_rgb(0,0,0,0.16)] backdrop-blur md:max-w-xl md:gap-4"
    >
      <p className="min-w-0 flex-1 text-xs leading-snug text-foreground/75">
        Optional analytics — no form entries collected.{" "}
        <a href="/privacy" className="font-semibold text-primary hover:underline">Privacy</a>
      </p>
      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          data-testid="consent-decline"
          onClick={() => choose(false)}
          className="rounded-full border border-foreground/20 px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-foreground hover:text-background"
        >
          Decline
        </button>
        <button
          type="button"
          data-testid="consent-accept"
          onClick={() => choose(true)}
          className="rounded-full bg-primary px-4 py-1.5 text-xs font-heading font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
        >
          Accept
        </button>
      </div>
    </div>
  );
}
